/**
 * Tag catalog for the analytics layer. Tag VALUES must stay low-cardinality
 * (Clarity dashboard filter shows ~24 suggested values; >24 is type-only filter).
 * Money/counts are ALWAYS bucketed — never raw amounts (cardinality + financial leak).
 * Clarity tag values are `string | string[]`.
 */

// ── Persistent dimension keys (set via identify traits) ──────────────
export const TAG = {
  userType: 'userType',
  authMethod: 'authMethod',
  accountTier: 'accountTier',
  hasFreeBets: 'hasFreeBets',
  playedDemo: 'playedDemo',
  balanceBucket: 'balanceBucket',
  referredBy: 'referredBy',
  utmSource: 'utmSource',
  utmMedium: 'utmMedium',
  utmCampaign: 'utmCampaign',
  deviceType: 'deviceType',
} as const

export type UserType = 'visitor' | 'authenticated'
export type AuthMethod = 'email' | 'google' | 'wallet_evm' | 'wallet_solana'
export type AccountTier = 'copper' | 'bronze' | 'silver' | 'gold' | 'platinum' | 'diamond'
export type DeviceType = 'mobile' | 'desktop'
export type BoolTag = 'true' | 'false'

/** demo_played funnel stage: session entry vs. every subsequent placement. */
export type DemoStage = 'started' | 'bet'
/** deposit_method_selected: which top-level tab, and the concrete method card. */
export type DepositCategory = 'crypto' | 'fiat'
export type DepositMethodTag =
  | 'fiat' | 'wallet' | 'direct'
  | 'bitolo_spei' | 'bitolo_oxxo' | 'bitolo_enefevo'
  // `lightning` was emitted here long before it was declared. `speed` is the branded
  // card that now covers every Speed rail, so this event answers "which card", and
  // per-rail attribution comes from `deposit_started`, which sends the rail as
  // `provider`. Dashboards keyed on `lightning` alone need repointing.
  | 'lightning' | 'speed'
  | 'no_suitable_option'
/** voucher_action: redeem vs. forfeit, and which surface fired it. */
export type VoucherAction = 'applied' | 'removed'
export type VoucherSurface = 'deposit' | 'wallet'

export type BalanceBucket = '0' | '<$10' | '$10-50' | '$50-200' | '$200+'
export type StakeBucket = '<$1' | '$1-5' | '$5-25' | '$25-100' | '$100+'
export type AmountBucket = '<$20' | '$20-100' | '$100-500' | '$500+'
export type MultiplierBucket = '<1.5x' | '1.5-2x' | '2-5x' | '5-20x' | '20x+'
export type PayoutBucket = '<$10' | '$10-100' | '$100-1000' | '$1000+'

/** Dimensions that ride on every event once set (via identify traits). */
export interface PersistentTags {
  userId?: string
  visitorId?: string
  userType?: UserType
  authMethod?: AuthMethod
  accountTier?: AccountTier
  hasFreeBets?: BoolTag
  /** Sticky once the visitor has played a demo round (localStorage-backed) —
   *  rides into post-signup identify so registered users are segmentable by
   *  whether they demoed first (Demo Player → Registered funnel). */
  playedDemo?: BoolTag
  balanceBucket?: BalanceBucket
  referredBy?: string
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  deviceType?: DeviceType
}

export function bucketBalance(usd: number): BalanceBucket {
  if (usd <= 0) return '0'
  if (usd < 10) return '<$10'
  if (usd < 50) return '$10-50'
  if (usd < 200) return '$50-200'
  return '$200+'
}

export function bucketStake(usd: number): StakeBucket {
  if (usd < 1) return '<$1'
  if (usd < 5) return '$1-5'
  if (usd < 25) return '$5-25'
  if (usd < 100) return '$25-100'
  return '$100+'
}

export function bucketAmount(usd: number): AmountBucket {
  if (usd < 20) return '<$20'
  if (usd < 100) return '$20-100'
  if (usd < 500) return '$100-500'
  return '$500+'
}

export function bucketMultiplier(x: number): MultiplierBucket {
  if (x < 1.5) return '<1.5x'
  if (x < 2) return '1.5-2x'
  if (x < 5) return '2-5x'
  if (x < 20) return '5-20x'
  return '20x+'
}

export function bucketPayout(usd: number): PayoutBucket {
  if (usd < 10) return '<$10'
  if (usd < 100) return '$10-100'
  if (usd < 1000) return '$100-1000'
  return '$1000+'
}
